import { useEffect, useState } from 'react';
import fetchWrapper from '../lib/fetch-wrapper';
import { useProfile } from '../lib/profileContext';
import { isLocalDev } from '../lib/isLocalDev';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';

function generateAbbreviation(companyName: string | null | undefined): string {
  if (!companyName) {
    return "Unknown";
  }
  if (companyName.includes('/')) {
    return "MGR/PGR";
  }
  if (companyName.includes('-')) {
    companyName = companyName.split('-')[0];
  }
  const words = companyName.split(/\s+/);
  return words.slice(0, 4).map(word => word[0].toUpperCase()).join("") || "Unknown";
}

function isNotAbbreviation(property: string | null | undefined): string {
  if (!property) {
    return "Unknown";
  }
  const isNotAbbr = [...property].some(char => char >= 'a' && char <= 'z');
  if (isNotAbbr) {
    return generateAbbreviation(property);
  }
  return property;
}

const AuthCheck = ({ children }: { children: React.ReactNode }) => {
  const [authorized, setAuthorized] = useState(false);
  const { authState, oktaAuth } = useOktaAuth();
  const { updateProfile } = useProfile();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const checkAuth = async () => {
      if (!authState || authState.isPending) return;

      if (window.location.pathname.includes('/login/callback')) return;

      if (!authState.isAuthenticated) {
        console.log("User is not authenticated. Redirecting to login...");
        oktaAuth.signInWithRedirect();
        return;
      }

      try {
        const user = await oktaAuth.getUser();
        const uid = user.sub;

        const fw = new fetchWrapper();
        const profile = await fw.getProfile(uid);
        const storage_profile = localStorage.getItem("profile");

        const profileState = storage_profile
            ? JSON.parse(storage_profile)
            : {
              firstName: profile.data.givenName,
              lastName: profile.data.surname,
              email: profile.data.mail,
              property: isNotAbbreviation(
                  String(profile.data.officeLocation || profile.data.companyName)
              ),
              employeeId: profile.data.employeeId,
              department: profile.data.department,
            };

        if (storage_profile) {
          profileState.property = isNotAbbreviation(
              profileState.property || profileState.companyName
          );
        }

        updateProfile(profileState);

        // Seed localStorage so x-profile-test is sent on subsequent requests.
        if (isLocalDev() && !storage_profile) {
          localStorage.setItem('profile', JSON.stringify(profileState));
        }

        await i18n.changeLanguage('en');

        setAuthorized(true);
      } catch (error) {
        console.error("Auth check failed:", error);
      }
    };

    checkAuth();
  }, [authState, i18n, oktaAuth]);

  return <div className="App">{authorized ? children : null}</div>;
};

export default AuthCheck;